import React, { useRef, useMemo } from "react"
import { useReactToPrint } from "react-to-print"
import { useParams } from "react-router-dom"

// Existing quotes
import mtsData, { quoteData as mtsQuote } from "../data/quoteData-MTS.js"
import pbpData, { quoteData as pbpQuote } from "../data/quoteData-PBP.js"
// Rollbusch
import rqmData, { quoteData as rqmQuote } from "../data/quoteData-RQM.js"
// JMDS
import jmdsData, { quoteData as jmdsQuote } from "../quotes/jmdsQuoteData.js"
// ESP
import esppressoData, { quoteData as esppressoQuote } from "../quotes/esppressoQuoteData.js"
// Morningside
import morningsideQuoteData, {
  quoteData as morningsideQuote,
} from "../quotes/morningsideQuoteData.js"

// ---------- Header ----------
function Header({ title, quoteNumber }) {
  return (
    <header className="bg-[#0E1526] text-white rounded-lg p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 print:rounded-none">
      <div className="space-y-1">
        <div className="text-2xl font-semibold leading-none">
          <span className="text-white">2</span>
          <span className="text-blue-400">merative</span>
        </div>
        <div className="uppercase tracking-widest text-xs text-gray-300">
          Iterate. Create. Elevate.
        </div>
      </div>
      <div className="text-right space-y-1 md:min-w-[280px]">
        {title && <div className="text-2xl font-semibold">{title}</div>}
        {quoteNumber && (
          <div className="text-xs text-gray-300 mt-1">Quote #: {quoteNumber}</div>
        )}
      </div>
    </header>
  )
}

// ---------- Recurring Note ----------
const RecurringNote = () => (
  <p className="text-xs text-gray-500 mt-2">
    * Monthly and yearly costs are recurring charges for hosting, SSL, and basic maintenance (
    <span className="font-semibold">Year 1 included in one-off quote</span>).
  </p>
)

// ---------- Helpers ----------
const currency = (n) =>
  (Number(n) || 0).toLocaleString("en-AU", { style: "currency", currency: "AUD" })

const calcTotals = (sections = {}) => {
  const oneOffArr = Array.isArray(sections.oneOff) ? sections.oneOff : []
  const recurArr = Array.isArray(sections.recurring) ? sections.recurring : []

  const sum = (arr, key) => arr.reduce((acc, row) => acc + (Number(row?.[key]) || 0), 0)

  const oneOff = sum(oneOffArr, "cost")
  const recurringMonthly = sum(recurArr, "costMonthly")
  const recurringYearly = sum(recurArr, "costYearly")

  return { oneOff, recurringMonthly, recurringYearly }
}

// ---------- Slug Selection ----------
function useQuoteDataBySlug() {
  const { slug } = useParams()
  const s = (slug || "").toLowerCase()

  if (s === "mts") return mtsQuote || mtsData
  if (s === "pbp" || s === "") return pbpQuote || pbpData
  if (s === "rqm" || s === "rollbusch") return rqmQuote || rqmData

  if (s === "jmds" || s === "jmd" || s === "john-martin") return jmdsQuote || jmdsData

  if (s === "esppresso" || s === "esp") return esppressoQuote || esppressoData

  if (s === "morningside" || s === "mor" || s === "morningside-chiro")
    return morningsideQuote || morningsideQuoteData

  return pbpQuote || pbpData
}

// ---------- Main ----------
export default function Quote() {
  const contentRef = useRef()
  const data = useQuoteDataBySlug()

  const meta = data?.meta ?? {}
  const business = data?.business ?? {}

  const notes = Array.isArray(data?.notes) ? data.notes : []
  const assumptions = Array.isArray(data?.assumptionsExclusions) ? data.assumptionsExclusions : []
  const warranty = Array.isArray(data?.warrantySupport) ? data.warrantySupport : []
  const acceptance = data?.acceptance ?? {}

  const options = useMemo(() => (Array.isArray(data?.options) ? data.options : []), [data?.options])

  const totals = useMemo(
    () =>
      options.map((opt) => ({
        label: opt?.label ?? "Untitled Option",
        ...calcTotals(opt?.sections),
      })),
    [options]
  )

  const handlePrint = useReactToPrint({
  documentTitle: meta.quoteNumber || meta.title || "Quote",
  contentRef,
  removeAfterPrint: true,
})


  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div ref={contentRef} className="bg-white rounded-lg shadow">
        <Header title={meta.title} quoteNumber={meta.quoteNumber} />

        {Array.isArray(meta.summary) && meta.summary.length > 0 && (
          <section className="p-6 border-b border-gray-200 text-sm">
            <h3 className="font-semibold text-gray-700 mb-2">Summary</h3>
            {meta.summary.map((para, i) => (
              <p key={i} className="mb-2" dangerouslySetInnerHTML={{ __html: para }} />
            ))}
          </section>
        )}

        <section className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
          <div>
            <h3 className="font-semibold text-gray-700 mb-2">Prepared For</h3>
            {meta.clientName && <div>{meta.clientName}</div>}
            {meta.url && (
              <div>
                <a href={meta.url} target="_blank" rel="noreferrer" className="text-blue-600 underline">
                  {meta.url}
                </a>
              </div>
            )}
            {meta.quoteNumber && <div>Quote #: {meta.quoteNumber}</div>}
            {meta.date && <div>Date: {meta.date}</div>}
          </div>

          <div className="md:text-right">
            <h3 className="font-semibold text-gray-700 mb-2">Prepared By</h3>
            {business.name && <div>{business.name}</div>}
            {business.abn && <div>ABN: {business.abn}</div>}
            {business.email && <div>{business.email}</div>}
            {business.phone && <div>{business.phone}</div>}
            {meta.preparedBy && <div>Consultant: {meta.preparedBy}</div>}
            {meta.auditedBy && <div>Audited by: {meta.auditedBy}</div>}
          </div>
        </section>

        {options.map((opt, idx) => {
          const t = totals[idx]
          const oneOff = opt?.sections?.oneOff ?? []
          const recurring = opt?.sections?.recurring ?? []
          const approach = opt?.sections?.projectApproach ?? []

          return (
            <section key={opt?.label ?? idx} className="p-6 border-t border-gray-200">
              <h2 className="text-xl font-bold mb-4 border-l-4 border-blue-600 pl-3">
                {opt?.label ?? "Untitled Option"}
              </h2>

              {approach.length > 0 && (
                <div className="mb-4 text-sm">
                  <h4 className="font-semibold text-gray-700 mb-2 border-l-4 border-blue-500 pl-2 bg-blue-50 rounded">
                    Project Approach
                  </h4>
                  <ul className="list-disc ml-5 space-y-1">
                    {approach.map((a, i) => (
                      <li key={i}>{a}</li>
                    ))}
                  </ul>
                </div>
              )}

              <h3 className="font-semibold text-gray-700 mt-4 mb-2 border-l-4 border-blue-500 pl-2 bg-blue-50 rounded">
                One-off Costs
              </h3>

              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b">
                    <th className="py-2">Item</th>
                    <th className="py-2 text-right">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {oneOff.map((row, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-2">{row?.item ?? "Untitled"}</td>
                      <td className="py-2 text-right">{currency(row?.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <h3 className="font-semibold text-gray-700 mt-6 mb-2 border-l-4 border-blue-500 pl-2 bg-blue-50 rounded">
                Recurring Costs
              </h3>

              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b">
                    <th className="py-2">Item</th>
                    <th className="py-2 text-right">Monthly</th>
                    <th className="py-2 text-right">Yearly</th>
                  </tr>
                </thead>
                <tbody>
                  {recurring.map((row, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-2">{row?.item ?? "Untitled"}</td>
                      <td className="py-2 text-right">
                        {row?.costMonthly ? currency(row.costMonthly) : "-"}
                      </td>
                      <td className="py-2 text-right">
                        {row?.costYearly ? currency(row.costYearly) : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <RecurringNote />

              <div className="mt-6 bg-gray-50 p-4 rounded shadow-sm text-sm">
                <h4 className="font-semibold mb-2 text-blue-700">Totals</h4>
                <div className="flex justify-between">
                  <span>One-off:</span>
                  <span>{currency(t.oneOff)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Monthly:</span>
                  <span>{currency(t.recurringMonthly)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Yearly:</span>
                  <span>{currency(t.recurringYearly)}</span>
                </div>
              </div>
            </section>
          )
        })}

        {notes.length > 0 && (
          <section className="p-6 border-t border-gray-200 text-sm">
            <h3 className="font-semibold text-gray-700 mb-2 border-l-4 border-blue-500 pl-2 bg-blue-50 rounded">
              Notes
            </h3>
            <ul className="list-disc ml-5 space-y-1">
              {notes.map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
          </section>
        )}

        {assumptions.length > 0 && (
          <section className="p-6 border-t border-gray-200 text-sm">
            <h3 className="font-semibold text-gray-700 mb-2 border-l-4 border-blue-500 pl-2 bg-blue-50 rounded">
              Assumptions & Exclusions
            </h3>
            <ul className="list-disc ml-5 space-y-1">
              {assumptions.map((a, i) => (
                <li key={i}>{a}</li>
              ))}
            </ul>
          </section>
        )}

        {warranty.length > 0 && (
          <section className="p-6 border-t border-gray-200 text-sm">
            <h3 className="font-semibold text-gray-700 mb-2 border-l-4 border-blue-500 pl-2 bg-blue-50 rounded">
              Warranty & Support
            </h3>
            <ul className="list-disc ml-5 space-y-1">
              {warranty.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </section>
        )}

        <section className="p-6 border-t border-gray-200 text-sm">
          <h3 className="font-semibold text-gray-700 mb-2 border-l-4 border-blue-500 pl-2 bg-blue-50 rounded">
            Acceptance & Payment Details
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-1">
            <div>
              <strong>Bank:</strong> ANZ Business Account
            </div>
            <div>
              <strong>Account Name:</strong> 2mer Pathways
            </div>
            <div>
              <strong>BSB:</strong> 015-214
            </div>
            <div>
              <strong>Account Number:</strong> 802397505
            </div>
            <div className="sm:col-span-2">
              <strong>Reference:</strong> Quote #: {meta.quoteNumber}
            </div>
            <div className="sm:col-span-2 mt-2 border-t pt-2">
              {acceptance?.validUntil && (
                <div>
                  <strong>Valid until:</strong> {acceptance.validUntil}
                </div>
              )}
              {acceptance?.paymentTerms && (
                <div>
                  <strong>Payment terms:</strong> {acceptance.paymentTerms}
                </div>
              )}
            </div>
          </div>
          <p className="mt-4 text-gray-600">
            Acceptance and project scheduling will be confirmed once the deposit payment has been received.
            A full invoice will be issued for your records.
          </p>
        </section>
      </div>

      <div className="mt-6 flex justify-end print:hidden">
        <button
          onClick={handlePrint}
          className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition"
        >
          Print / Save as PDF
        </button>
      </div>
    </div>
  )
}
